if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      lastOptionValuesSignature = '';
      pdpDebugEnabled = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      productNavigationHydrationPromise = undefined;
      productNavigationIdleId = undefined;
      productNavigationTimeoutId = undefined;
      productNavigationLoadHandler = undefined;
      productNavigationInteractionHandler = undefined;
      productNavigationElement = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.lastOptionValuesSignature = this.getOptionValuesSignature();
        this.initQuantityHandlers();
        this.debugLog('connectedCallback', {
          productId: this.dataset.productId,
          url: this.dataset.url,
          updateUrl: this.dataset.updateUrl,
          optionSignature: this.lastOptionValuesSignature,
        });
        this.scheduleProductNavigationHydration();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      scheduleProductNavigationHydration() {
        const navigation = this.querySelector('[data-product-navigation]');
        if (!navigation || navigation.getAttribute('data-product-navigation-ready') === 'true') return;

        this.productNavigationElement = navigation;
        this.productNavigationInteractionHandler = () => this.startProductNavigationHydration();

        ['pointerdown', 'focusin'].forEach((eventName) => {
          navigation.addEventListener(eventName, this.productNavigationInteractionHandler, { once: true, passive: true });
        });

        const scheduleWhenIdle = () => {
          this.productNavigationLoadHandler = undefined;

          if ('requestIdleCallback' in window) {
            this.productNavigationIdleId = window.requestIdleCallback(
              () => this.startProductNavigationHydration(),
              { timeout: 5000 }
            );
            return;
          }

          this.productNavigationTimeoutId = window.setTimeout(
            () => this.startProductNavigationHydration(),
            1500
          );
        };

        if (document.readyState === 'complete') {
          scheduleWhenIdle();
        } else {
          this.productNavigationLoadHandler = scheduleWhenIdle;
          window.addEventListener('load', scheduleWhenIdle, { once: true });
        }
      }

      clearProductNavigationSchedule() {
        if (this.productNavigationElement && this.productNavigationInteractionHandler) {
          ['pointerdown', 'focusin'].forEach((eventName) => {
            this.productNavigationElement.removeEventListener(eventName, this.productNavigationInteractionHandler);
          });
        }

        if (this.productNavigationLoadHandler) {
          window.removeEventListener('load', this.productNavigationLoadHandler);
        }

        if (this.productNavigationIdleId !== undefined && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.productNavigationIdleId);
        }

        if (this.productNavigationTimeoutId !== undefined) {
          window.clearTimeout(this.productNavigationTimeoutId);
        }

        this.productNavigationElement = undefined;
        this.productNavigationInteractionHandler = undefined;
        this.productNavigationLoadHandler = undefined;
        this.productNavigationIdleId = undefined;
        this.productNavigationTimeoutId = undefined;
      }

      startProductNavigationHydration() {
        if (this.productNavigationHydrationPromise) return this.productNavigationHydrationPromise;

        this.clearProductNavigationSchedule();
        this.productNavigationHydrationPromise = this.hydrateProductNavigation().finally(() => {
          this.productNavigationHydrationPromise = undefined;
        });

        return this.productNavigationHydrationPromise;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      getShopRoot() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }

      normalizeNavigationHandles(value) {
        return [
          ...new Set(
            (value || '')
              .split(/[|,\s]+/)
              .map((handle) => handle.trim())
              .filter(Boolean)
          ),
        ];
      }

      async fetchCollectionProducts(collectionHandle) {
        if (!collectionHandle) return [];

        window.__perfumiarzCollectionProductsCache = window.__perfumiarzCollectionProductsCache || new Map();
        const cache = window.__perfumiarzCollectionProductsCache;
        if (cache.has(collectionHandle)) return cache.get(collectionHandle);

        const limit = 250;
        const maxPages = 8;
        const products = [];
        const seenHandles = new Set();

        for (let page = 1; page <= maxPages; page += 1) {
          try {
            const response = await fetch(
              `${this.getShopRoot()}collections/${encodeURIComponent(
                collectionHandle
              )}/products.json?limit=${limit}&page=${page}`,
              { headers: { Accept: 'application/json' } }
            );

            if (!response.ok) break;

            const data = await response.json();
            const pageProducts = Array.isArray(data.products) ? data.products : [];
            let addedProducts = 0;

            pageProducts.forEach((product) => {
              if (!product?.handle || seenHandles.has(product.handle)) return;
              seenHandles.add(product.handle);
              products.push(product);
              addedProducts += 1;
            });

            if (pageProducts.length < limit || addedProducts === 0) break;
          } catch (error) {
            break;
          }
        }

        cache.set(collectionHandle, products);
        return products;
      }

      buildProductUrl(productHandle) {
        return `${this.getShopRoot()}products/${encodeURIComponent(productHandle)}`;
      }

      async hydrateProductNavigation() {
        const navigation = this.querySelector('[data-product-navigation]');
        if (!navigation || navigation.getAttribute('data-product-navigation-ready') === 'true') return;

        const currentProductHandle = navigation.getAttribute('data-product-navigation-product-handle') || '';
        const collectionHandles = this.normalizeNavigationHandles(
          navigation.getAttribute('data-product-navigation-collections')
        );
        if (!currentProductHandle || collectionHandles.length === 0) return;

        const prevLink = navigation.querySelector('[data-product-nav-link="prev"]');
        const nextLink = navigation.querySelector('[data-product-nav-link="next"]');
        if (!prevLink || !nextLink) return;

        for (const collectionHandle of collectionHandles) {
          const products = await this.fetchCollectionProducts(collectionHandle);
          const currentIndex = products.findIndex((product) => product?.handle === currentProductHandle);
          if (currentIndex === -1 || products.length <= 1) continue;

          const prevProduct = products[(currentIndex - 1 + products.length) % products.length];
          const nextProduct = products[(currentIndex + 1) % products.length];
          if (!prevProduct?.handle || !nextProduct?.handle) continue;

          prevLink.href = this.buildProductUrl(prevProduct.handle);
          nextLink.href = this.buildProductUrl(nextProduct.handle);
          navigation.setAttribute('data-product-navigation-ready', 'true');
          return;
        }
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.clearProductNavigationSchedule();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          this.debugLog('postProcessHtmlCallbacks', {
            newNodeId: newNode?.id || '',
            newNodeClass: newNode?.className || '',
          });
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const selectedOptionValuesSignature = this.getOptionValuesSignature(selectedOptionValues);
        this.debugLog('handleOptionValueChange:start', {
          targetId: target?.id || '',
          targetValue: target?.value || '',
          targetType: target?.type || target?.tagName || '',
          isTrusted: event?.isTrusted,
          selectedOptionValues,
          selectedOptionValuesSignature,
          lastOptionValuesSignature: this.lastOptionValuesSignature,
        });

        if (selectedOptionValuesSignature && selectedOptionValuesSignature === this.lastOptionValuesSignature) {
          this.debugLog('handleOptionValueChange:skip-duplicate-signature', {
            selectedOptionValuesSignature,
          });
          return;
        }
        if (selectedOptionValuesSignature) this.lastOptionValuesSignature = selectedOptionValuesSignature;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

        this.debugLog('handleOptionValueChange:renderProductInfo', {
          productUrl,
          requestUrl,
          shouldSwapProduct,
          shouldFetchFullPage,
        });

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
          debugMeta: {
            shouldSwapProduct,
            shouldFetchFullPage,
            productUrl,
            selectedOptionValuesSignature,
          },
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        this.debugLog('resetProductFormState');
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.debugLog('handleSwapProduct', {
            productUrl,
            updateFullPage,
            selector,
            variantId: variant?.id || null,
          });
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, debugMeta = {} }) {
        const requestStartedAt = performance.now();
        this.debugLog('renderProductInfo:fetch:start', { requestUrl, targetId, ...debugMeta });
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            this.debugLog('renderProductInfo:fetch:success', {
              requestUrl,
              durationMs: Math.round(performance.now() - requestStartedAt),
              responseLength: responseText?.length || 0,
            });
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            this.debugLog('renderProductInfo:focus-restored', { targetId });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              this.debugLog('renderProductInfo:fetch:aborted', { requestUrl });
            } else {
              this.debugLog('renderProductInfo:fetch:error', {
                requestUrl,
                message: error?.message || String(error),
              });
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getOptionValuesSignature(optionValues = this.variantSelectors?.selectedOptionValues || []) {
        if (!Array.isArray(optionValues) || optionValues.length === 0) return '';
        return optionValues.filter(Boolean).join(',');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          this.debugLog('updateOptionValues:viewTransition', {
            oldVariantSelectsId: this.variantSelectors?.id || '',
            newVariantSelectsId: variantSelects?.id || '',
          });
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.debugLog('handleUpdateProductInfo', {
            productUrl,
            variantId: variant?.id || null,
            featuredMediaId: variant?.featured_media?.id || null,
          });

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variant) {
        const variantId = variant?.id;
        this.debugLog('updateVariantInputs', { variantId: variantId || null });
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));

          const compareAtPriceInput = productForm.querySelector('[data-compare-at-price-property]');
          if (compareAtPriceInput) compareAtPriceInput.value = variant?.compare_at_price ?? '';
        });
      }

      updateURL(url, variantId) {
        this.debugLog('updateURL', { url, variantId: variantId || null });
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.debugLog('setUnavailable');
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        this.debugLog('updateMedia:start', { variantFeaturedMediaId: variantFeaturedMediaId || null });
        if (!variantFeaturedMediaId) {
          this.debugLog('updateMedia:skip-no-featured-media');
          return;
        }

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;
          let insertedCount = 0;
          let removedCount = 0;
          let movedCount = 0;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
              insertedCount += 1;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
              removedCount += 1;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              movedCount += 1;

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });

          this.debugLog('updateMedia:sync-summary', {
            sourceCount: mediaGallerySourceItems.length,
            destinationCount: mediaGalleryDestinationItems.length,
            shouldRefresh,
            insertedCount,
            removedCount,
            movedCount,
          });
        }

        // set featured media as active in the media gallery
        this.debugLog('updateMedia:setActiveMedia', {
          mediaId: `${this.dataset.section}-${variantFeaturedMediaId}`,
          prepend: true,
        });
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      isDebugEnabled() {
        if (this.pdpDebugEnabled !== undefined) return this.pdpDebugEnabled;
        let enabled = false;

        try {
          const searchParams = new URLSearchParams(window.location.search);
          enabled =
            searchParams.get('pdp_debug') === '1' ||
            window.localStorage.getItem('pdp_debug') === '1' ||
            window.__PDP_DEBUG === true;
        } catch (error) {
          enabled = window.__PDP_DEBUG === true;
        }

        this.pdpDebugEnabled = enabled;
        return this.pdpDebugEnabled;
      }

      debugLog(message, payload = {}) {
        if (!this.isDebugEnabled()) return;
        const timestamp = new Date().toISOString();
        const sectionLabel = this.sectionId || this.dataset.section || 'unknown-section';
        console.log(`[PDP-DEBUG ${timestamp}][${sectionLabel}] ${message}`, payload);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
